import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { useIsMobile } from "@utils/hooks";
import { ArrowIcon } from "@ui/components/icons";
import { Bloc } from "@ui/components/layout";
import { PdfViewer } from "@ui/components/media/pdf/pdfViewer";

interface CarouselPdfViewerProps extends React.HTMLAttributes<HTMLDivElement> {
	source: string;
	pdfHeight: number;
	showWings?: boolean;
	reportPdfWith?: (width: number) => void;
	reportPageHeight?: (width: number) => void;
	fullScreen?: boolean;
	fullScreenSetter: () => void;
}
export const CarouselPdfViewer: React.FC<CarouselPdfViewerProps> = ({
	source,
	pdfHeight,
	reportPdfWith,
	reportPageHeight,
	fullScreen,
	fullScreenSetter,
}) => {
	const [currentPdfIndexObj, setCurrentPdfIndexObj] = useState<{
		previous?: number;
		current?: number;
		next?: number;
		pageHeight: number;
		pageWidth: number;
		previousFn?: () => void;
		nextFn?: () => void;
		fullScreenFn?: unknown;
	}>();
	const isMobile = useIsMobile();

	useEffect(() => {
		if (currentPdfIndexObj?.pageHeight && !!reportPageHeight) reportPageHeight(currentPdfIndexObj.pageHeight);
	}, [currentPdfIndexObj?.pageHeight]);

	return !isMobile ? (
		<>
			<FirstWing>
				<PdfViewer
					forcedWidth={currentPdfIndexObj?.pageWidth}
					forcedHeight={currentPdfIndexObj?.pageHeight}
					source={source}
					pdfHeight={pdfHeight}
					forcedIndex={currentPdfIndexObj?.previous}
					hideArrows
				/>
			</FirstWing>
			<MainWing>
				{currentPdfIndexObj && !!currentPdfIndexObj.nextFn && (
					<StyledNextArrowIcon
						small
						onClick={() =>
							currentPdfIndexObj && !!currentPdfIndexObj.nextFn ? currentPdfIndexObj.nextFn() : undefined
						}
					>
						<ArrowIcon />
					</StyledNextArrowIcon>
				)}
				{currentPdfIndexObj && !!currentPdfIndexObj.previousFn && (
					<StyledPreviousArrowIcon
						small
						onClick={() =>
							currentPdfIndexObj && !!currentPdfIndexObj.previousFn ? currentPdfIndexObj.previousFn() : undefined
						}
					>
						<ArrowIcon />
					</StyledPreviousArrowIcon>
				)}
				<PdfViewer
					source={source}
					pdfHeight={pdfHeight}
					reportPdfWith={reportPdfWith}
					reportIndex={(value) => setCurrentPdfIndexObj(value)}
					hideArrows
					fullScreen={fullScreen}
					fullScreenSetter={fullScreenSetter}
				/>
			</MainWing>
			<LastWing>
				<PdfViewer
					forcedHeight={currentPdfIndexObj?.pageHeight}
					forcedWidth={currentPdfIndexObj?.pageWidth}
					source={source}
					pdfHeight={pdfHeight}
					forcedIndex={currentPdfIndexObj?.next}
					hideArrows
				/>
			</LastWing>
		</>
	) : (
		<PdfViewer source={source} pdfHeight={pdfHeight} />
	);
};

const FirstWing = styled(Bloc)`
	min-width: 625px;
	max-width: 625px;
`;

const MainWing = styled(Bloc)`
	min-width: 625px;
	max-width: 625px;
	margin-right: 10px !important;
	margin-left: 10px;
`;

const LastWing = styled(Bloc)`
	min-width: 625px;
	max-width: 625px;
`;

const ArrowWrapper = styled.div<{ onClick?: () => void; small?: boolean }>`
	cursor: pointer;
	${({ onClick }) =>
		!onClick &&
		css`
			opacity: 0;
			cursor: initial;
		`};
	width: ${({ small }) => (small ? 30 : 40)}px;
	height: ${({ small }) => (small ? 30 : 40)}px;
	border-radius: 20px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: ${({ theme }) => theme.colors.secondaryLight};
	svg  {
		width: ${({ small }) => (small ? 14 : 18)}px;
		transition: transform ease-in-out 0.2s;
		.main-path {
			fill: ${({ theme }) => theme.colors.secondary};
		}
	}
	&:hover svg {
		transform: translateX(-2px);
	}
	&:last-child {
		transform: rotate(180deg);
		background-color: ${({ theme }) => theme.colors.secondary};
		svg  {
			.main-path {
				fill: ${({ theme }) => theme.colors.white};
			}
		}
	}
`;
const StyledPreviousArrowIcon = styled(ArrowWrapper)`
	position: absolute;
	left: -15%;
	top: 45%;
	z-index: 1;
	cursor: pointer;

	@media (max-width: 1110px) {
		left: 5%;
		top: 45%;
	}

	@media (max-width: 980px) {
		left: 10%;
		top: 45%;
	}
`;

const StyledNextArrowIcon = styled(ArrowWrapper)`
	position: absolute;
	right: -15%;
	top: 45%;
	z-index: 1;
	cursor: pointer;
	-moz-transform: scale(-1, 1);
	-webkit-transform: scale(-1, 1);
	-o-transform: scale(-1, 1);
	-ms-transform: scale(-1, 1);
	transform: scale(-1, 1);
	@media (max-width: 1110px) {
		right: 5%;
		top: 45%;
	}

	@media (max-width: 980px) {
		right: 10%;
		top: 45%;
	}
`;
